import { omitBy, isNil } from 'lodash';
import {
  RiReplyFill,
  RiPencilFill,
  RiDeleteBin5Fill,
  RiCheckLine,
  RiCloseLine,
} from 'react-icons/ri';
import { FaRobot } from 'react-icons/fa';
import Card from '../../../components/Card';
import Typography from '../../../components/Typography';
import { useUserSession } from '../../../components/UserSession';
import Page from '../../../layouts/DashboardLayout/Page';
import {
  useReviewsControllerGoogleReviews,
  useReviewsControllerReplyAllGoogleReviews,
  useReviewsControllerReplyGoogleReview,
} from '../../../api/informApi/informApiComponents';
import {
  GoogleReviewReply,
  SerializedGoogleReview,
  UpdateGoogleReviewDto,
} from '../../../api/informApi/informApiSchemas';
import { timeAgo } from '../../../utils';
import Pagination from '../../../components/Pagination';
import { useEffect, useRef, useState } from 'react';
import Button from '../../../components/Button';
import { toast } from 'react-hot-toast';
import IconButton from '../../../components/IconButton';
import Textarea from '../../../components/Textarea';
import Rating from '../../../components/Rating';
import styles from './Reviews.module.scss';

const PAGE_SIZE = 10;
const MORE_THRESHOLD = 2;

interface ReviewProps {
  onReplyUpdate?: (newReview: GoogleReviewReply) => void;
  data: SerializedGoogleReview;
}
function Review({ onReplyUpdate, data }: ReviewProps) {
  const userCtx = useUserSession();
  const name = data.user.name ?? 'Anonymous';
  const createdAt = new Date(data.createdAt);
  const { mutateAsync } = useReviewsControllerReplyGoogleReview();
  const [timestamp, unit] = timeAgo(createdAt, 'minutes') ?? [];
  const displayDate = new Intl.DateTimeFormat('en-CA').format(createdAt);
  const showDate = !['minutes', 'hours', 'days'].includes(unit as any);
  const [disabled, setDisabled] = useState(false);
  const [edit, setEdit] = useState<string | undefined>();
  const inEditMode = edit !== undefined;
  const hasComment = !!data.comment;
  const hasReply = !!data.reply;

  const runMutation = async (
    body: UpdateGoogleReviewDto,
    toastOpts: { loading: string; success: string; error: string },
  ) => {
    setDisabled(true);
    const newReview = await toast
      .promise(
        mutateAsync({
          pathParams: {
            id: userCtx.currentOrganization!,
            reviewId: data.id,
          },
          body,
        }),
        toastOpts,
      )
      .finally(() => setDisabled(false));
    if (!body.dry) onReplyUpdate?.(newReview);
    return newReview;
  };

  return (
    <li className={styles.review}>
      <div className={styles.profile}>
        <img
          src={data.user.image}
          alt={`Profile of ${name}`}
          width="32"
          height="32"
        />
        <Typography
          className={styles.name}
          textWeight="bold"
          textType="p2"
          as="h3"
        >
          {name ?? 'Anonymous'}
        </Typography>
      </div>
      <div className={styles.rating}>
        <Rating rating={data.rating} />
        <Typography textColor="text-2" textType="p4" as="span">
          {showDate ? displayDate : timestamp}
        </Typography>
      </div>
      <div className={styles.comment}>
        <Typography
          className={hasComment || styles.empty}
          textColor={hasComment ? 'text-1' : 'text-2'}
          textType="p2"
          as="p"
        >
          {data.comment ?? 'No Comment'}
        </Typography>
        {hasReply || inEditMode ? (
          <div className={styles.reply}>
            <div className={styles.replyText}>
              <Typography textType="p2" textWeight="bold" as="p">
                Reply
              </Typography>
              {inEditMode ? (
                <Textarea
                  className={styles.input}
                  onChange={(e) => setEdit(e.currentTarget.value)}
                  rows={8}
                  value={edit}
                />
              ) : (
                <Typography className={styles.replyData} textType="p2" as="p">
                  {data.reply}
                </Typography>
              )}
            </div>
            <div className={styles.replyActions}>
              <IconButton
                onClick={async () => {
                  if (inEditMode) {
                    await runMutation(
                      { type: 'manual', reply: edit },
                      {
                        success: 'Reply updated!',
                        error: 'Unable to update reply. Please try again later',
                        loading: 'Updating reply...',
                      },
                    );
                    setEdit(undefined);
                  } else {
                    setEdit(data.reply);
                  }
                }}
                disabled={disabled || (inEditMode && !edit)}
                icon={inEditMode ? RiCheckLine : RiPencilFill}
                buttonColor={inEditMode ? 'success' : 'primary-1'}
                variant="solid"
              />
              <IconButton
                onClick={() => {
                  if (inEditMode) {
                    setEdit(undefined);
                  } else {
                    runMutation(
                      { type: 'manual', reply: null },
                      {
                        success: 'Reply deleted!',
                        error: 'Unable to delete reply. Please try again later',
                        loading: 'Deleting reply...',
                      },
                    );
                  }
                }}
                disabled={disabled}
                icon={inEditMode ? RiCloseLine : RiDeleteBin5Fill}
                buttonColor="error"
                variant="solid"
              />
            </div>
          </div>
        ) : (
          <Button
            onClick={async () => {
              const { comment } = await runMutation(
                { type: 'auto', reply: null, dry: true },
                {
                  success: 'Reply generated!',
                  error: 'Unable to generate reply. Please try again later',
                  loading: 'Generating reply...',
                },
              );
              setEdit(comment);
            }}
            className={styles.generate}
            disabled={disabled}
            icon={{
              element: RiReplyFill,
              placement: 'left',
            }}
            buttonColor="primary-1"
            variant="ghost"
          >
            Generate Reply
          </Button>
        )}
      </div>
    </li>
  );
}

function Reviews() {
  const userCtx = useUserSession();
  const [page, setPage] = useState(0);
  const [reviews, setReviews] = useState<SerializedGoogleReview[]>([]);
  const nextToken = useRef<string>();
  const { isLoading: isReplyingAll, mutateAsync } =
    useReviewsControllerReplyAllGoogleReviews();
  const { data, refetch, isLoading } = useReviewsControllerGoogleReviews(
    {
      pathParams: {
        id: userCtx.currentOrganization!,
      },
      queryParams: omitBy(
        {
          nextPageToken: nextToken.current,
        },
        isNil,
      ),
    },
    { enabled: false },
  );

  const start = page * PAGE_SIZE;
  const end = start + PAGE_SIZE;

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (isLoading) return;
    setReviews((_reviews) => [
      ...(nextToken.current ? _reviews : []),
      ...(data?.reviews ?? []),
    ]);
    nextToken.current = data?.nextPageToken;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <Page
      heading="Dashboard"
      loading={isLoading && !nextToken.current}
      actions={[
        {
          onClick() {
            toast.promise(
              mutateAsync({
                pathParams: {
                  id: userCtx.currentOrganization!,
                },
              }),
              {
                success: 'All reviews replied! Please refresh in a few seconds',
                loading: 'Replying to all reviews...',
                error: 'Something happened. Please try again later',
              },
            );
          },
          children: 'Automate Response to All',
          icon: {
            placement: 'left',
            element: FaRobot,
          },
          buttonColor: 'success',
          variant: 'solid',
          disabled: isReplyingAll,
        },
      ]}
      disabled={isReplyingAll}
    >
      <Typography textType="h4" as="h2">
        This Week's Reviews
      </Typography>
      <div className={styles.layout}>
        <Card className={styles.reviews} backgroundColor="background-1" as="ul">
          {reviews.slice(start, end).map((review, i) => (
            <Review
              onReplyUpdate={(newReview) => {
                setReviews((reviews) => {
                  const id = start + i;
                  const newReviews = [...reviews];
                  newReviews[id] = {
                    ...reviews[id],
                    reply: newReview.comment,
                  };
                  return newReviews;
                });
              }}
              data={review}
              key={review.id}
            />
          ))}
        </Card>
        <Pagination
          className={styles.pagination}
          items={reviews ?? []}
          itemsPerPage={PAGE_SIZE}
          onPageChange={(newPage, totalPages) => {
            if (newPage + MORE_THRESHOLD >= totalPages) refetch();
            setPage(newPage);
          }}
          hasMore={!!nextToken.current}
          currentPage={page}
        />
      </div>
    </Page>
  );
}

Reviews.routeProps = {
  path: '/reviews',
};
export default Reviews;
